import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { absoluteUrl } from "@/components/PinItButton";
import PinItButton from "@/components/PinItButton";
import ProductCard from "@/components/ProductCard";
import usePageMeta, { readingTime } from "@/lib/seo";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setError("Post not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    api.get("/blog?limit=10").then(({ data }) => {
      setRelated(data.filter((p) => p.slug !== post.slug).slice(0, 3));
    });
    api.get("/products?trending=true&limit=6").then(({ data }) => {
      setProducts(data.slice(0, 4));
    });
  }, [post]);

  usePageMeta(
    post
      ? {
          title: post.title,
          description: post.excerpt,
          image: `${process.env.REACT_APP_BACKEND_URL}/api/share/article/${post.slug}.jpg`,
          type: "article",
        }
      : undefined
  );

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 text-[#636769]">Loading…</div>;
  if (error || !post) return <div className="max-w-3xl mx-auto px-6 py-24 text-[#636769]" data-testid="post-not-found">Post not found.</div>;

  const mins = readingTime(post.content);
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <>
      <article data-testid="blog-post-page" className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#636769] hover:text-[#2D3032] mb-8">
          <ArrowLeft size={14} /> Back to journal
        </Link>
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-4">
          <span>{post.author}</span>
          <span className="text-[#E6E1D6]">/</span>
          <span className="flex items-center gap-1.5 text-[#636769]"><Clock size={12} strokeWidth={1.8} /> {mins} min read</span>
        </div>
        <h1 className="font-heading text-4xl md:text-5xl text-[#2D3032] font-light tracking-tight leading-[1.05]">{post.title}</h1>
        <p className="mt-6 text-lg text-[#636769] leading-relaxed">{post.excerpt}</p>

        <figure className="relative aspect-[16/9] my-10 overflow-hidden bg-[#F4F1EA]">
          <img src={absoluteUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover gc-sunwash" />
          <div className="absolute top-4 right-4">
            <PinItButton pageUrl={pageUrl} mediaUrl={`${process.env.REACT_APP_BACKEND_URL}/api/share/article/${post.slug}.jpg`} description={`${post.title} — ${post.excerpt}`} testid="pin-article-hero" />
          </div>
        </figure>

        <p className="text-[10px] uppercase tracking-[0.24em] text-[#8EAD9E] mb-6">
          This article may contain affiliate links — we may earn from qualifying purchases.
        </p>

        <div className="gc-article max-w-none text-[#2D3032] text-base md:text-[17px] leading-[1.85] font-body space-y-5">
          {post.content.split(/\n{2,}/).map((para, i) => (
            <p key={i}>{para.trim()}</p>
          ))}
        </div>

        {post.tags?.length > 0 && (
          <div className="flex gap-2 flex-wrap mt-10 pt-8 border-t border-[#E6E1D6]">
            {post.tags.map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-[0.2em] text-[#8EAD9E] border border-[#E6E1D6] px-3 py-1.5">#{t}</span>
            ))}
          </div>
        )}
      </article>

      {/* Featured Products inline */}
      {products.length > 0 && (
        <section className="bg-white border-y border-[#E6E1D6]" data-testid="article-featured-products">
          <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-[#EBAA86] mb-2">Featured in this story</p>
                <h2 className="font-heading text-2xl md:text-3xl text-[#2D3032] font-normal">The gear we keep coming back to.</h2>
              </div>
              <button
                type="button"
                data-testid="pin-all-products-btn"
                onClick={() => {
                  const galleryUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/article-pins/${post.slug}.html`;
                  const url = `https://www.pinterest.com/pin/find/?url=${encodeURIComponent(galleryUrl)}`;
                  window.open(url, "pinterest-pin-all", "width=900,height=820,scrollbars=yes");
                }}
                className="inline-flex items-center gap-2 bg-[#E60023] text-white px-5 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#AD081B] active:scale-95 transition-all duration-200"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z"/></svg>
                Pin every product
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="related-articles">
          <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-2">Keep reading</p>
          <h2 className="font-heading text-2xl md:text-3xl text-[#2D3032] font-normal mb-10">More from the journal.</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {related.map((r) => (
              <Link key={r.id} to={`/blog/${r.slug}`} data-testid={`related-${r.slug}`} className="group block gc-card gc-fade">
                <div className="aspect-[4/5] overflow-hidden bg-[#F4F1EA] mb-4">
                  <img src={absoluteUrl(r.cover_image)} alt={r.title} loading="lazy" className="w-full h-full object-cover gc-sunwash group-hover:scale-[1.04] transition-transform duration-700" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#8EAD9E] mb-2">{readingTime(r.content)} min read</p>
                <h3 className="font-heading text-lg text-[#2D3032] font-medium leading-tight group-hover:text-[#8EAD9E] transition-colors">{r.title}</h3>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#EBAA86]">
                  Read <ArrowUpRight size={12} strokeWidth={1.8} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
